import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { Box, Typography, Card, Chip, IconButton, CardContent, Grid, Tooltip as TooltipCustom, useMediaQuery, Backdrop, CircularProgress, useTheme, Stack } from "@mui/material";
import { Menu as MenuIcon, Help as HelpIcon } from "@mui/icons-material";
// app UI controller for toggling sidenav
import { useMaterialUIController, setMiniSidenav } from "context";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import SouthIcon from "@mui/icons-material/South";
import datadrilldown from "assets/images/selectedicons/DataDrillDown.svg";
import drillDownArrow from "assets/images/selectedicons/DrillDownArrow.svg";
import FilterPanelContainer from "components/Filter/Filterpopup";
import KpiMetricscards from "components/KPI/KpiMetricscards";
import TimeSeriescards from "components/TimeSeries/TimeSeriescards";
import HighlightsCards from "components/Highlights/Highlightscard";
import Visualizations from "components/Visualizations/Visualizations";
import PieLegendCard from "components/Visualizations/Piecharts";
import Barcharts from "components/Visualizations/Barcharts";
import LineChartSubBrand from "components/Visualizations/Linechartsubbrand";
import LineChartsale from "components/Visualizations/LineChartsale";
import InventoryCharts from "components/Visualizations/Inventorycharts";
import WaterfallChart from "components/Visualizations/waterfallchart";
import TopSkuBarChart from "components/Visualizations/SkuBarchart";
import SkuSingleBarChart from "components/Visualizations/SingleBarchartSku";
import HiringFunnel from "components/Visualizations/Funnelchart";
import FilterChip from "components/FilterChip/FilterChip";
import { getTimeSeriesLabels } from "../../../components/TimeSeries/monthUtils";
import { fetchHumanResources } from "services/humanResources.service";

const HumanResourcesDashboard = ({ subcategoryToken: propSubcategoryToken }) => {
  const { lastYearSameMonth, currentMonthLabel, prevMonthLabel } = getTimeSeriesLabels();
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav } = controller;
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md")); // Below 900px
  const isTablet = useMediaQuery(theme.breakpoints.between("md", "lg")); // Between 900px and 1200px

  // State for API data
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [humanResourcesData, setHumanResourcesData] = useState(null);
  const [MyHumanResponseData, setMyHumanResponseData] = useState(null);

  useEffect(() => {
    const header = document.querySelector(".MuiAppBar-root");
    if (header) {
      header.style.display = "none";
    }
  }, []);

  // Toggle sidenav from the page menu icon
  const isTogglingSidenavRef = useRef(false);
  const handleToggleSidenav = useCallback(() => {
    if (isTogglingSidenavRef.current) return;
    isTogglingSidenavRef.current = true;
    // Defer to next frame to avoid blocking heavy charts paint
    requestAnimationFrame(() => {
      setMiniSidenav(dispatch, !miniSidenav);
      setTimeout(() => {
        isTogglingSidenavRef.current = false;
      }, 150);
    });
  }, [dispatch, miniSidenav]);

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setLoading(true);
        // Call fetchNetSales API and store first item
        const myHumanResponse = await fetchHumanResources();
        console.log("My Human Src API Response:", myHumanResponse);

        const myHumanItem = myHumanResponse || null;
        setMyHumanResponseData(myHumanItem);
        console.log(myHumanItem, "testtest");
      } catch (error) {
        console.error("Failed loading static mocks or transforming data:", error);
        setError("Failed to load static mock data. Check competitive_analysis/data/allMocks.json");
      } finally {
        setLoading(false);
      }
    };
    fetchAllData();
  }, [location.search, propSubcategoryToken]);
  const formatToMillions = (value) => {
    return value == null || value === "-" ? "-" : (Number(value) / 1_000_000).toFixed(1);
  };

  const truncateDecimal = (num, decimals = 2) => {
    const factor = 10 ** decimals;
    return (Math.floor(num * factor) / factor).toFixed(decimals);
  };

  const formatToK = (value, decimals = 1) => {
    const n = Number(value ?? 0);

    if (n === 0) return "0";
    if (n < 1000) return n.toFixed(decimals);

    return `${(n / 1000).toFixed(decimals)}`;
  };

  // KPI cards data
  const Kpiscardsdata = [
    {
      title: "Revenue Per Employee",
      values: [
        {
          label: "MTD",
          change: MyHumanResponseData?.revenuePerEmployees?.items?.cMvsGoal ? `${MyHumanResponseData?.revenuePerEmployees?.items?.cMvsGoal}%` : "0%",
          changely: MyHumanResponseData?.revenuePerEmployees?.items?.cMvsLM ? `${MyHumanResponseData?.revenuePerEmployees?.items?.cMvsLM}%` : "0%",
          value: `${formatToK(MyHumanResponseData?.revenuePerEmployees?.items?.currentMonth)}K`,
          status: "increase",
          title: "Month",
          lyValue: `$${formatToMillions(MyHumanResponseData?.revenuePerEmployees?.items?.lastMonth)}M`,
          glvalue: `$${formatToMillions(MyHumanResponseData?.revenuePerEmployees?.items?.goalCurrentMonth)}M`,
        },
        {
          label: "QTD",
          change: MyHumanResponseData?.revenuePerEmployees?.items?.cQvsGoal ? `${MyHumanResponseData?.revenuePerEmployees?.items?.cQvsGoal}%` : "0%",
          changely: MyHumanResponseData?.revenuePerEmployees?.items?.cQvsLQ ? `${MyHumanResponseData?.revenuePerEmployees?.items?.cQvsLQ}%` : "0%",
          value: `${formatToK(MyHumanResponseData?.revenuePerEmployees?.items?.currentQuarter)}K`,
          status: "increase",
          title: "Quarter",
          lyValue: `$${formatToMillions(MyHumanResponseData?.revenuePerEmployees?.items?.lastQuarter)}M`,
          glvalue: `$${formatToMillions(MyHumanResponseData?.revenuePerEmployees?.items?.goalCurrentQuarter)}M`,
        },
        {
          label: "YTD",
          change: MyHumanResponseData?.revenuePerEmployees?.items?.cYvsGoal ? `${MyHumanResponseData?.revenuePerEmployees?.items?.cYvsGoal}%` : "0%",
          changely: MyHumanResponseData?.revenuePerEmployees?.items?.cYvsLY ? `${MyHumanResponseData?.revenuePerEmployees?.items?.cYvsLY}%` : "0%",
          value: `${formatToK(MyHumanResponseData?.revenuePerEmployees?.items?.currentYear)}K`,
          status: "increase",
          title: "Year",
          lyValue: `$${formatToMillions(MyHumanResponseData?.revenuePerEmployees?.items?.lastYear)}M`,
          glvalue: `$${formatToMillions(MyHumanResponseData?.revenuePerEmployees?.items?.goalCurrentYear)}M`,
        },
      ],
    },
    {
      title: "Employee Turnover Rate",
      values: [
        {
          label: "MTD",
          change: MyHumanResponseData?.headcounts?.items?.cMvsGoal ? `${MyHumanResponseData?.headcounts?.items?.cMvsGoal}%` : "0%",
          changely: MyHumanResponseData?.headcounts?.items?.cMvsLM ? `${MyHumanResponseData?.headcounts?.items?.cMvsLM}%` : "0%",
          value: `${(((MyHumanResponseData?.headcounts?.items?.currentMonth ?? 0) / 10000) * 100).toFixed(1).replace("0.0", "0")}%`,
          status: "increase",
          title: "Month",
          lyValue: `$${formatToMillions(MyHumanResponseData?.headcounts?.items?.lastMonth)}M`,
          glvalue: `$${formatToMillions(MyHumanResponseData?.headcounts?.items?.goalCurrentMonth)}M`,
        },
        {
          label: "QTD",
          change: MyHumanResponseData?.headcounts?.items?.cQvsGoal ? `${MyHumanResponseData?.headcounts?.items?.cQvsGoal}%` : "0%",
          changely: MyHumanResponseData?.headcounts?.items?.cQvsLQ ? `${MyHumanResponseData?.headcounts?.items?.cQvsLQ}%` : "0%",
          value: `${(((MyHumanResponseData?.headcounts?.items?.currentQuarter ?? 0) / 10000) * 100).toFixed(1).replace("0.0", "0")}%`,
          status: "increase",
          title: "Quarter",
          lyValue: `$${formatToMillions(MyHumanResponseData?.headcounts?.items?.lastQuarter)}M`,
          glvalue: `$${formatToMillions(MyHumanResponseData?.headcounts?.items?.goalCurrentQuarter)}M`,
        },
        {
          label: "YTD",
          change: MyHumanResponseData?.headcounts?.items?.cYvsGoal ? `${MyHumanResponseData?.headcounts?.items?.cYvsGoal}%` : "0%",
          changely: MyHumanResponseData?.headcounts?.items?.cYvsLY ? `${MyHumanResponseData?.headcounts?.items?.cYvsLY}%` : "0%",
          value: `${(((MyHumanResponseData?.headcounts?.items?.currentYear ?? 0) / 10000) * 100).toFixed(1).replace("0.0", "0")}%`,
          status: "increase",
          title: "Year",
          lyValue: `$${formatToMillions(MyHumanResponseData?.headcounts?.items?.lastYear)}M`,
          glvalue: `$${formatToMillions(MyHumanResponseData?.headcounts?.items?.goalCurrentYear)}M`,
        },
      ],
    },
    {
      title: "Safety Incident Rate (TRIR)",
      values: [
        {
          label: "MTD",
          change: MyHumanResponseData?.triRs?.items?.cMvsGoal ? `${MyHumanResponseData?.triRs?.items?.cMvsGoal}%` : "0%",
          changely: MyHumanResponseData?.triRs?.items?.cMvsLM ? `${MyHumanResponseData?.triRs?.items?.cMvsLM}%` : "0%",
          value: truncateDecimal(MyHumanResponseData?.triRs?.items?.currentMonth ?? 0),
          status: "increase",
          title: "Month",
          lyValue: `$${formatToMillions(MyHumanResponseData?.triRs?.items?.lastMonth)}M`,
          glvalue: `$${formatToMillions(MyHumanResponseData?.triRs?.items?.goalCurrentMonth)}M`,
        },
        {
          label: "QTD",
          change: MyHumanResponseData?.triRs?.items?.cQvsGoal ? `${MyHumanResponseData?.triRs?.items?.cQvsGoal}%` : "0%",
          changely: MyHumanResponseData?.triRs?.items?.cQvsLQ ? `${MyHumanResponseData?.triRs?.items?.cQvsLQ}%` : "0%",
          value: truncateDecimal(MyHumanResponseData?.triRs?.items?.currentQuarter ?? 0),
          status: "increase",
          title: "Quarter",
          lyValue: `$${formatToMillions(MyHumanResponseData?.triRs?.items?.lastQuarter)}M`,
          glvalue: `$${formatToMillions(MyHumanResponseData?.triRs?.items?.goalCurrentQuarter)}M`,
        },
        {
          label: "YTD",
          change: MyHumanResponseData?.triRs?.items?.cYvsGoal ? `${MyHumanResponseData?.triRs?.items?.cYvsGoal}%` : "0%",
          changely: MyHumanResponseData?.triRs?.items?.cYvsLY ? `${MyHumanResponseData?.triRs?.items?.cYvsLY}%` : "0%",
          value: truncateDecimal(MyHumanResponseData?.triRs?.items?.currentYear ?? 0),
          status: "decrease",
          title: "Year",
          lyValue: `$${formatToMillions(MyHumanResponseData?.triRs?.items?.lastYear)}M`,
          glvalue: `$${formatToMillions(MyHumanResponseData?.triRs?.items?.goalCurrentYear)}M`,
        },
      ],
    },
    {
      title: "Time to Fill (Days)",
      values: [
        {
          label: "MTD",
          change: MyHumanResponseData?.timeToFills?.items?.cMvsGoal ? `${MyHumanResponseData?.timeToFills?.items?.cMvsGoal}%` : "0%",
          changely: MyHumanResponseData?.timeToFills?.items?.cMvsLM ? `${MyHumanResponseData?.timeToFills?.items?.cMvsLM}%` : "0%",
          value: Math.round(MyHumanResponseData?.timeToFills?.items?.currentMonth ?? 0),
          status: "increase",
          title: "Month",
          lyValue: `$${formatToMillions(MyHumanResponseData?.timeToFills?.items?.lastMonth)}M`,
          glvalue: `$${formatToMillions(MyHumanResponseData?.timeToFills?.items?.goalCurrentMonth)}M`,
        },
        {
          label: "QTD",
          change: MyHumanResponseData?.timeToFills?.items?.cQvsGoal ? `${MyHumanResponseData?.timeToFills?.items?.cQvsGoal}%` : "0%",
          changely: MyHumanResponseData?.timeToFills?.items?.cQvsLQ ? `${MyHumanResponseData?.timeToFills?.items?.cQvsLQ}%` : "0%",
          value: Math.round(MyHumanResponseData?.timeToFills?.items?.currentQuarter ?? 0),
          status: "increase",
          title: "Quarter",
          lyValue: `$${formatToMillions(MyHumanResponseData?.timeToFills?.items?.lastQuarter)}M`,
          glvalue: `$${formatToMillions(MyHumanResponseData?.timeToFills?.items?.goalCurrentQuarter)}M`,
        },
        {
          label: "YTD",
          change: MyHumanResponseData?.timeToFills?.items?.cYvsGoal ? `${MyHumanResponseData?.timeToFills?.items?.cYvsGoal}%` : "0%",
          changely: MyHumanResponseData?.timeToFills?.items?.cYvsLY ? `${MyHumanResponseData?.timeToFills?.items?.cYvsLY}%` : "0%",
          value: Math.round(MyHumanResponseData?.timeToFills?.items?.currentYear ?? 0),
          status: "decrease",
          title: "Year",
          lyValue: `$${formatToMillions(MyHumanResponseData?.timeToFills?.items?.lastYear)}M`,
          glvalue: `$${formatToMillions(MyHumanResponseData?.timeToFills?.items?.goalCurrentYear)}M`,
        },
      ],
    },
    {
      title: "Internal Fill Rate",
      values: [
        {
          label: "MTD",
          change: MyHumanResponseData?.internalFillRates?.items?.cMvsGoal ? `${MyHumanResponseData?.internalFillRates?.items?.cMvsGoal}%` : "0%",
          changely: MyHumanResponseData?.internalFillRates?.items?.cMvsLM ? `${MyHumanResponseData?.internalFillRates?.items?.cMvsLM}%` : "0%",
          value: `${(MyHumanResponseData?.internalFillRates?.items?.currentMonth ?? 0).toFixed(1).replace("0.0", "0")}%`,
          status: "increase",
          title: "Month",
          lyValue: `$${formatToMillions(MyHumanResponseData?.internalFillRates?.items?.lastMonth)}M`,
          glvalue: `$${formatToMillions(MyHumanResponseData?.internalFillRates?.items?.goalCurrentMonth)}M`,
        },
        {
          label: "QTD",
          change: MyHumanResponseData?.headcounts?.items?.cQvsGoal ? `${MyHumanResponseData?.headcounts?.items?.cQvsGoal}%` : "0%",
          changely: MyHumanResponseData?.headcounts?.items?.cQvsLQ ? `${MyHumanResponseData?.headcounts?.items?.cQvsLQ}%` : "0%",
          value: `${(MyHumanResponseData?.internalFillRates?.items?.currentQuarter ?? 0).toFixed(1).replace("0.0", "0")}%`,
          status: "increase",
          title: "Quarter",
          lyValue: `$${formatToMillions(MyHumanResponseData?.internalFillRates?.items?.lastQuarter)}M`,
          glvalue: `$${formatToMillions(MyHumanResponseData?.internalFillRates?.items?.goalCurrentQuarter)}M`,
        },
        {
          label: "YTD",
          change: MyHumanResponseData?.internalFillRates?.items?.cYvsGoal ? `${MyHumanResponseData?.internalFillRates?.items?.cYvsGoal}%` : "0%",
          changely: MyHumanResponseData?.internalFillRates?.items?.cYvsLY ? `${MyHumanResponseData?.internalFillRates?.items?.cYvsLY}%` : "0%",
          value: `${(MyHumanResponseData?.internalFillRates?.items?.currentYear ?? 0).toFixed(1).replace("0.0", "0")}%`,
          status: "increase",
          title: "Year",
          lyValue: `$${formatToMillions(MyHumanResponseData?.internalFillRates?.items?.lastYear)}M`,
          glvalue: `$${formatToMillions(MyHumanResponseData?.internalFillRates?.items?.goalCurrentYear)}M`,
        },
      ],
    },
    {
      title: "Employee Engagement Score",
      values: [
        {
          label: "MTD",
          change: MyHumanResponseData?.engagementScores?.items?.cMvsGoal ? `${MyHumanResponseData?.engagementScores?.items?.cMvsGoal}%` : "0%",
          changely: MyHumanResponseData?.engagementScores?.items?.cMvsLM ? `${MyHumanResponseData?.engagementScores?.items?.cMvsLM}%` : "0%",
          value: Math.round(MyHumanResponseData?.engagementScores?.items?.currentMonth ?? 0),
          status: "increase",
          title: "Month",
          lyValue: `$${formatToMillions(MyHumanResponseData?.engagementScores?.items?.lastMonth)}M`,
          glvalue: `$${formatToMillions(MyHumanResponseData?.engagementScores?.items?.goalCurrentMonth)}M`,
        },
        {
          label: "QTD",
          change: MyHumanResponseData?.engagementScores?.items?.cQvsGoal ? `${MyHumanResponseData?.engagementScores?.items?.cQvsGoal}%` : "0%",
          changely: MyHumanResponseData?.engagementScores?.items?.cQvsLQ ? `${MyHumanResponseData?.engagementScores?.items?.cQvsLQ}%` : "0%",
          value: Math.round(MyHumanResponseData?.engagementScores?.items?.currentQuarter ?? 0),
          status: "increase",
          title: "Quarter",
          lyValue: `$${formatToMillions(MyHumanResponseData?.engagementScores?.items?.lastQuarter)}M`,
          glvalue: `$${formatToMillions(MyHumanResponseData?.engagementScores?.items?.goalCurrentQuarter)}M`,
        },
        {
          label: "YTD",
          change: MyHumanResponseData?.engagementScores?.items?.cYvsGoal ? `${MyHumanResponseData?.engagementScores?.items?.cYvsGoal}%` : "0%",
          changely: MyHumanResponseData?.engagementScores?.items?.cYvsLY ? `${MyHumanResponseData?.engagementScores?.items?.cYvsLY}%` : "0%",
          value: Math.round(MyHumanResponseData?.engagementScores?.items?.currentYear ?? 0),
          status: "decrease",
          title: "Year",
          lyValue: `$${formatToMillions(MyHumanResponseData?.engagementScores?.items?.lastYear)}M`,
          glvalue: `$${formatToMillions(MyHumanResponseData?.engagementScores?.items?.goalCurrentYear)}M`,
        },
      ],
    },
  ];

  //second section cards
  const timeSeriesCardsdata = [
    {
      title: "Revenue Per Employee",
      values: [
        {
          label: lastYearSameMonth,
          change: MyHumanResponseData?.revenuePerEmployees?.items?.cmcYvsCMLY ? `${truncateDecimal(MyHumanResponseData?.revenuePerEmployees?.items?.cmcYvsCMLY)}%` : "0%",
          value: `${formatToK(MyHumanResponseData?.revenuePerEmployees?.items?.sameMonthLastYear)}K`,
          status: "increase",
          arrow: "right",
        },
        {
          label: currentMonthLabel,
          change: null,
          value: `${formatToK(MyHumanResponseData?.revenuePerEmployees?.items?.currentMonth)}K`,
          status: "nochange",
          arrow: null,
        },
        {
          label: prevMonthLabel,
          change: MyHumanResponseData?.revenuePerEmployees?.items?.cMvsLM ? `${truncateDecimal(MyHumanResponseData?.revenuePerEmployees?.items?.cMvsLM)}%` : "0%",
          value: `${formatToK(MyHumanResponseData?.revenuePerEmployees?.items?.lastMonth)}K`,
          status: "increase",
          arrow: "left",
        },
      ],
    },
    {
      title: "Employee Turnover Rate",
      values: [
        {
          label: lastYearSameMonth,
          change: MyHumanResponseData?.headcounts?.items?.cmcYvsCMLY ? `${truncateDecimal(MyHumanResponseData?.headcounts?.items?.cmcYvsCMLY)}%` : "0%",
          value: `${(((MyHumanResponseData?.headcounts?.items?.sameMonthLastYear ?? 0) / 10000) * 100).toFixed(1).replace("0.0", "0")}%`,
          status: "decrease",
          arrow: "right",
        },
        {
          label: currentMonthLabel,
          change: null,
          value: `${(((MyHumanResponseData?.headcounts?.items?.currentMonth ?? 0) / 10000) * 100).toFixed(1).replace("0.0", "0")}%`,
          status: "nochange",
          arrow: null,
        },
        {
          label: prevMonthLabel,
          change: MyHumanResponseData?.headcounts?.items?.cMvsLM ? `${truncateDecimal(MyHumanResponseData?.headcounts?.items?.cMvsLM)}%` : "0%",
          value: `${(((MyHumanResponseData?.headcounts?.items?.lastMonth ?? 0) / 10000) * 100).toFixed(1).replace("0.0", "0")}%`,
          status: "decrease",
          arrow: "left",
        },
      ],
    },
    {
      title: "Safety Incident Rate (TRIR)",
      values: [
        {
          label: lastYearSameMonth,
          change: MyHumanResponseData?.triRs?.items?.cmcYvsCMLY ? `${truncateDecimal(MyHumanResponseData?.triRs?.items?.cmcYvsCMLY)}%` : "0%",
          value: truncateDecimal(MyHumanResponseData?.triRs?.items?.sameMonthLastYear ?? 0),
          status: "decrease",
          arrow: "right",
        },
        {
          label: currentMonthLabel,
          change: null,
          value: truncateDecimal(MyHumanResponseData?.triRs?.items?.currentMonth ?? 0),
          status: "nochange",
          arrow: null,
        },
        {
          label: prevMonthLabel,
          change: MyHumanResponseData?.triRs?.items?.cMvsLM ? `${truncateDecimal(MyHumanResponseData?.triRs?.items?.cMvsLM)}%` : "0%",
          value: truncateDecimal(MyHumanResponseData?.triRs?.items?.lastMonth ?? 0),
          status: "decrease",
          arrow: "left",
        },
      ],
    },
    {
      title: "Time to Fill (Days)",
      values: [
        {
          label: lastYearSameMonth,
          change: MyHumanResponseData?.timeToFills?.items?.cmcYvsCMLY ? `${truncateDecimal(MyHumanResponseData?.timeToFills?.items?.cmcYvsCMLY)}%` : "0%",
          value: Math.round(MyHumanResponseData?.timeToFills?.items?.sameMonthLastYear ?? 0),
          status: "increase",
          arrow: "right",
        },
        {
          label: currentMonthLabel,
          change: null,
          value: Math.round(MyHumanResponseData?.timeToFills?.items?.currentMonth ?? 0),
          status: "nochange",
          arrow: null,
        },
        {
          label: prevMonthLabel,
          change: MyHumanResponseData?.timeToFills?.items?.cMvsLM ? `${truncateDecimal(MyHumanResponseData?.timeToFills?.items?.cMvsLM)}%` : "0%",
          value: Math.round(MyHumanResponseData?.timeToFills?.items?.lastMonth ?? 0),
          status: "decrease",
          arrow: "left",
        },
      ],
    },
    {
      title: "Internal Fill Rate",
      values: [
        {
          label: lastYearSameMonth,
          change: MyHumanResponseData?.internalFillRates?.items?.cmcYvsCMLY ? `${truncateDecimal(MyHumanResponseData?.internalFillRates?.items?.cmcYvsCMLY)}%` : "0%",
          value: `${(MyHumanResponseData?.internalFillRates?.items?.sameMonthLastYear ?? 0).toFixed(1).replace("0.0", "0")}%`,
          status: "increase",
          arrow: "right",
        },
        {
          label: currentMonthLabel,
          change: null,
          value: `${(MyHumanResponseData?.internalFillRates?.items?.currentMonth ?? 0).toFixed(1).replace("0.0", "0")}%`,
          status: "nochange",
          arrow: null,
        },
        {
          label: prevMonthLabel,
          change: MyHumanResponseData?.internalFillRates?.items?.cMvsLM ? `${truncateDecimal(MyHumanResponseData?.internalFillRates?.items?.cMvsLM)}%` : "0%",
          value: `${(MyHumanResponseData?.internalFillRates?.items?.lastMonth ?? 0).toFixed(1).replace("0.0", "0")}%`,
          status: "decrease",
          arrow: "left",
        },
      ],
    },
    {
      title: "Employee Engagement Score",
      values: [
        {
          label: lastYearSameMonth,
          change: MyHumanResponseData?.engagementScores?.items?.cmcYvsCMLY ? `${truncateDecimal(MyHumanResponseData?.engagementScores?.items?.cmcYvsCMLY)}%` : "0%",
          value: Math.round(MyHumanResponseData?.engagementScores?.items?.sameMonthLastYear ?? 0),
          status: "increase",
          arrow: "right",
        },
        {
          label: currentMonthLabel,
          change: null,
          value: Math.round(MyHumanResponseData?.engagementScores?.items?.currentMonth ?? 0),
          status: "nochange",
          arrow: null,
        },
        {
          label: prevMonthLabel,
          change: MyHumanResponseData?.engagementScores?.items?.cMvsLM ? `${truncateDecimal(MyHumanResponseData?.engagementScores?.items?.cMvsLM)}%` : "0%",
          value: Math.round(MyHumanResponseData?.engagementScores?.items?.lastMonth ?? 0),
          status: "decrease",
          arrow: "left",
        },
      ],
    },
  ];

  const [openFilters, setOpenFilters] = useState(false);
  const handleToggleFilters = () => {
    setOpenFilters((prev) => !prev);
  };

  // Filter state
  const [activeFilter, setActiveFilter] = useState("All");
  const [filterLoading, setFilterLoading] = useState(false);

  // Define filter presets for each quick filter
  const getFilterPreset = useCallback((filterName) => {
    const allCompanies = ["Nutrabolt", "Bloom"];
    const allCategories = ["Functional Beverage", "Active Nutrition"];
    const allBrands = ["C4", "Cellucor", "XTEND", "Bloom"];
    const allSubBrands = ["C4", "C4 Performance", "C4 Smart Energy", "C4 Ultimate", "XTEND", "Bloom Sparkling Energy", "Bloom Pop"];
    const allRoutes = ["Ecommerce", "Direct", "DSD"];
    const allChannels = ["Ecommerce", "Convenience Chain", "Convenience Independent", "Drug", "Food", "Multi Outlet", "Mass"];

    switch (filterName) {
      case "All":
        return {
          Company: allCompanies,
          Category: allCategories,
          Brand: allBrands,
          "Sub-Brand": allSubBrands,
          "Route to Market": allRoutes,
          Channel: allChannels,
        };
      default:
        return null;
    }
  }, []);

  const [filterPresets, setFilterPresets] = useState(() => getFilterPreset("All"));

  // Handle filter click with full-page loader
  const handleFilterClick = (filter) => {
    if (filter === activeFilter || filterLoading) return;

    setFilterLoading(true);
    setLoading(true); // Show full-page loader

    // Simulate loading delay
    setTimeout(() => {
      const preset = getFilterPreset(filter);
      setActiveFilter(filter);

      // Set the preset - this will trigger FilterPanelContainer to apply it
      if (preset) {
        setFilterPresets(preset);
      } else {
        setFilterPresets(null);
      }

      setFilterLoading(false);
      setLoading(false); // Hide full-page loader

      // Open filter panel for "Custom" filter
      if (filter === "Custom") {
        setOpenFilters(true);
      }
    }, 800);
  };

  // Handle filter changes from FilterPanelContainer
  const handleFiltersChange = useCallback(
    (newFilters) => {
      // Don't check for preset match if we're already in Custom mode
      // FilterPanelContainer's isApplyingPresetRef prevents callback when applying presets
      if (activeFilter === "Custom") return;

      // Check if the current filters match any preset
      const presetNames = ["All"];
      let matchedPreset = null;

      for (const presetName of presetNames) {
        const preset = getFilterPreset(presetName);
        if (preset) {
          const isMatching = Object.keys(preset).every((key) => {
            const presetValues = [...(preset[key] || [])].sort();
            const currentValues = [...(newFilters[key] || [])].sort();
            return presetValues.length === currentValues.length && presetValues.every((val, idx) => val === currentValues[idx]);
          });

          if (isMatching) {
            matchedPreset = presetName;
            break;
          }
        }
      }

      // If filters don't match any preset, switch to Custom
      if (!matchedPreset && activeFilter !== "Custom") {
        setActiveFilter("Custom");
        setFilterPresets(null);
      }
    },
    [activeFilter, getFilterPreset],
  );
  const [activeIdx, setActiveIdx] = React.useState(0);
  const kpiBriefData = [
    {
      title: "Month",
      value: "$ 65.2 M",
      lyValue: "59.12 M",
      pct: "5.8%",
      positive: true,
      status: "increase",
      glvalue: "$ 85.5 M",
    },
    {
      title: "Quarter",
      value: "$ 180.6 M",
      lyValue: "160.29 M",
      pct: "11.9%",
      positive: true,
      status: "increase",
      glvalue: "$ 207.3 M",
    },
    {
      title: "Year",
      value: "$ 859.1 M",
      lyValue: "823.03 M",
      pct: "5.6%",
      positive: true,
      status: "increase",
      glvalue: "$ 882.8 M",
    },
  ];

  const getDepartmentColor = (departmentName) => {
    const colorMap = {
      Finance: "#2E3BAB",
      Operations: "#3FAF6C",
      Product: "#F2A93B",
      IT: "#64719A",
      HR: "#8B5CF6",
      Marketing: "#E46BAA",
      Commercial: "#4A63E0",
    };

    return colorMap[departmentName?.trim()] || "#000000";
  };
  const headcountByDepartmentPie = useMemo(() => {
    const items = MyHumanResponseData?.headcountByDepartments?.items;

    if (!items || !Array.isArray(items) || items.length === 0) {
      return [];
    }

    return items.map((item) => ({
      label: item.departmentName || "",
      value: Number(((item.currentQuarterPercentage ?? 0) * 100).toFixed(1)),
      color: getDepartmentColor(item.departmentName),
    }));
  }, [MyHumanResponseData]);

  console.log(headcountByDepartmentPie, "headcountByDepartmentPie");

  const getRetentionColor = (label) => {
    const colorMap = {
      "Involuntary Departures": "#EB6383",
      "Retained Employees": "#45B97C",
      "Voluntary Departures": "#F4A62A",
    };

    return colorMap[label?.trim()] || "#000000";
  };
  const retentionRatePie = useMemo(() => {
    const items = MyHumanResponseData?.retentionRateMixes?.items;

    if (!Array.isArray(items) || items.length === 0) return [];

    return items.map((item) => ({
      label: item.category || "",
      value: Number(((item.currentQuarterPercentage ?? 0) * 100).toFixed(1)),
      color: getRetentionColor(item.category),
    }));
  }, [MyHumanResponseData]);
  console.log(retentionRatePie, "retentionRatePie");
  const getTenureColor = (label) => {
    const colorMap = {
      "< 1 year": "#B0C9FF",
      "1–3 years": "#6A7FDB",
      "3–5 years": "#2E4ACF",
      "5–10 years": "#1A2B8F",
      "10+ years": "#000000",
    };

    return colorMap[label?.trim()] || "#000000";
  };
  const tenureMixPie = useMemo(() => {
    const items = MyHumanResponseData?.tenureMixes?.items;

    if (!Array.isArray(items) || items.length === 0) return [];

    return items.map((item) => ({
      label: item.category || "",
      value: Number(((item.currentQuarterPercentage ?? 0) * 100).toFixed(1)),
      color: getTenureColor(item.category),
    }));
  }, [MyHumanResponseData]);
  console.log(tenureMixPie, "tenureMixPie");

  const getHiringSourceColor = (label) => {
    const colorMap = {
      "Job Boards": "#6A7FDB",
      "Recruiting Agencies": "#A26FDC",
      "Employee Referrals": "#45B97C",
      "Company Website": "#F4A62A",
      Social: "#EB6383",
    };

    return colorMap[label?.trim()] || "#000000";
  };
  const hiringSourcePie = useMemo(() => {
    const items = MyHumanResponseData?.hiringSourceMixes?.items;

    if (!Array.isArray(items) || items.length === 0) return [];

    return items.map((item) => ({
      label: item.category || "",
      value: Number(((item.currentQuarterPercentage ?? 0) * 100).toFixed(1)),
      color: getHiringSourceColor(item.category),
    }));
  }, [MyHumanResponseData]);
  console.log(hiringSourcePie, "hiringSourcePie");

  const getEmployeeCountDeptColor = (departmentName) => {
    const normalizedName = departmentName?.trim() || "";
    const colorMap = {
      Marketing: "#EB6383",
      Commercial: "#6A7FDB",
      Finance: "#2E4ACF",
      Operations: "#45B97C",
      Product: "#F4A62A",
      IT: "#5F6368",
      "Information Technology": "#5F6368",
      HR: "#A26FDC",
    };
    return colorMap[normalizedName] || "#000000";
  };

  const normalizeDepartmentName = (departmentName) => {
    const name = departmentName?.trim() || "";
    if (name === "Information Technology") return "IT";
    return name;
  };

  const employeeCountByDept = useMemo(() => {
    const items = MyHumanResponseData?.headcountByDepartmentMonthlies?.items;

    if (!items || !Array.isArray(items) || items.length === 0) {
      return {
        title: "Employee Count Stacked by Department",
        xLabels: [],
        series: [],
        dataMatrix: [],
        yTicks: [],
      };
    }

    // Group items by yearMonth
    const monthMap = new Map();
    items.forEach((item) => {
      const monthKey = item.yearMonth;
      if (!monthMap.has(monthKey)) {
        monthMap.set(monthKey, []);
      }
      monthMap.get(monthKey).push(item);
    });

    // Sort months chronologically
    const sortedMonths = Array.from(monthMap.keys()).sort((a, b) => {
      return new Date(a + "-01") - new Date(b + "-01");
    });

    // Create xLabels from yearMonth (format: "Feb. 2026")
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const xLabels = sortedMonths.map((yearMonth) => {
      const [year, month] = yearMonth.split("-");
      const monthIndex = parseInt(month, 10) - 1;
      return `${monthNames[monthIndex]}. ${year}`;
    });

    // Collect unique departments and normalize names
    const departmentSet = new Set();
    items.forEach((item) => {
      const normalized = normalizeDepartmentName(item.departmentName);
      if (normalized) departmentSet.add(normalized);
    });

    // Define department order (matching original series order)
    const departmentOrder = ["Marketing", "Commercial", "Finance", "Operations", "Product", "IT", "HR"];
    const departments = departmentOrder.filter((dept) => departmentSet.has(dept));

    // Add any remaining departments not in the predefined order
    departmentSet.forEach((dept) => {
      if (!departments.includes(dept)) {
        departments.push(dept);
      }
    });

    // Create series with colors
    const series = departments.map((dept) => ({
      label: dept,
      color: getEmployeeCountDeptColor(dept),
    }));

    // Build dataMatrix: each row is a month, each column is a department
    const dataMatrix = sortedMonths.map((yearMonth) => {
      const monthData = monthMap.get(yearMonth);
      const valueMap = new Map();

      monthData.forEach((item) => {
        const normalizedDept = normalizeDepartmentName(item.departmentName);
        valueMap.set(normalizedDept, item.employeeCount ?? 0);
      });

      return departments.map((dept) => valueMap.get(dept) ?? 0);
    });
    // Calculate stacked totals per month
    const stackedTotals = dataMatrix.map(row =>
      row.reduce((sum, val) => sum + val, 0)
    );

    // Get maximum stacked value
    const maxValue = Math.max(...stackedTotals);

    // Generate nice tick step
    function niceTickStep(maxVal, numTicks = 5) {
      const roughStep = maxVal / numTicks;
      const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
      const residual = roughStep / magnitude;

      let niceResidual;
      if (residual >= 5) niceResidual = 5;
      else if (residual >= 2) niceResidual = 2;
      else niceResidual = 1;

      return niceResidual * magnitude;
    }

    const tickStep = niceTickStep(maxValue, 5);
    const maxTick = tickStep * Math.ceil(maxValue / tickStep);

    const yTicks = [];
    for (let tick = 0; tick <= maxTick; tick += tickStep) {
      yTicks.push(tick);
    }

    console.log(yTicks, "yTicks")
    return {
      title: "Employee Count Stacked by Department",
      xLabels,
      series,
      dataMatrix,
      yTicks
    };
  }, [MyHumanResponseData]);

  const getHiringFunnelColor = (stage) => {
    const colorMap = {
      Applications: "#C95D54",
      "Resume Qualified": "#D9AD63",
      Interviewed: "#5DAAD2",
      "Offer Sent": "#5A74A8",
      Hired: "#4BAA8A",
    };

    return colorMap[stage?.trim()] || "#000000";
  };
  const hiringFunnelDatas = useMemo(() => {
    const items = MyHumanResponseData?.hiringFunnels?.items;

    if (!Array.isArray(items) || items.length === 0) {
      return { title: "Hiring Funnel", data: [] };
    }

    return {
      title: "Hiring Funnel",
      data: items.map((item) => ({
        label: item.stage || "",
        value: item.currentYear ?? 0,
        color: getHiringFunnelColor(item.stage),
      })),
    };
  }, [MyHumanResponseData]);

  console.log(hiringFunnelDatas, "hiringFunnelDatas");

  const getChannelColor = (channel) => {
    const colorMap = {
      "Job Boards": "#4E5FE3",
      "Recruitment Agencies": "#8B5CF6",
      "Employee Referrals": "#3CB371",
      "Company Website": "#F4A62A",
      Social: "#EC6BAA",
      "Campus Hiring": "#22C55E",
    };

    return colorMap[channel] || "#000000";
  };
  const applicationByRoleChartData = useMemo(() => {
    const items = MyHumanResponseData?.applicationByRoleSources?.items;

    if (!Array.isArray(items) || items.length === 0) return null;

    // 1. Y-axis labels (Roles)
    const yLabels = [...new Set(items.map((i) => i.roleName))];

    // 2. Series labels (Channels)
    const channelLabels = [...new Set(items.map((i) => i.channelName))];

    // Optional fixed order
    const channelOrder = ["Job Boards", "Recruitment Agencies", "Employee Referrals", "Company Website", "Social Media", "Internal Transfer", "Campus Hiring"];

    const sortedChannels = channelOrder.filter((c) => channelLabels.includes(c));

    // 3. Series with colors
    const series = sortedChannels.map((channel) => ({
      label: channel,
      color: getChannelColor(channel),
    }));

    // 4. Data Matrix (rows = roles, columns = channels)
    const dataMatrix = yLabels.map((role) =>
      sortedChannels.map((channel) => {
        const match = items.find((i) => i.roleName === role && i.channelName === channel);

        return match?.ytdValue ?? 0;
      }),
    );
    const rowTotals = dataMatrix.map((row) =>
      row.reduce((sum, value) => sum + value, 0)
    );

    // Get maximum total
    const maxValue = Math.max(...rowTotals);

    // Round nicely to nearest 50
    const roundedMax = Math.ceil(maxValue / 50) * 50;

    // Create 5 divisions
    const divisions = 5;
    const step = roundedMax / divisions;

    const xTicks = [];
    for (let i = 0; i <= divisions; i++) {
      xTicks.push(i * step);
    }


    return {
      title: "Application Count for Top 10 Open Roles",
      yLabels,
      series,
      dataMatrix,
      xTicks
    };
  }, [MyHumanResponseData]);

  console.log(applicationByRoleChartData, "applicationByRoleChartData");

  return (
    <Box
      sx={{
        ml: 0,
        transition: "margin-left 0.25s ease",
        "& .MuiBox-root": { backgroundImage: "none" },
      }}
      style={{
        padding: isMobile ? "8px" : "0px 16px 0px 16px",
        fontFamily: "inter !important",
      }}
    >
      {/* Loading Backdrop */}
      <Backdrop
        open={loading}
        sx={{
          position: "fixed",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: "rgba(255, 255, 255, 0.8)",
        }}
      >
        <CircularProgress color="primary" />
      </Backdrop>

      <Box
        sx={{
          height: {
            xs: "calc(100dvh - 140px)", // mobile header
            md: "calc(100vh - 64px)", // desktop header
          },
          overflowY: "auto",
          // px: isMobile ? 1 : 2,
          pb: "100px",
          /* smooth scrolling */
          scrollBehavior: "smooth",
          WebkitOverflowScrolling: "touch", // iOS momentum

          /* hide scrollbar */
          scrollbarWidth: "none", // Firefox
          msOverflowStyle: "none", // IE / Edge
          "&::-webkit-scrollbar": {
            display: "none", // Chrome / Safari
          },
        }}
      >
        {/* Section Title: KPI's */}
        <Typography sx={{ mb: 1, mt: 3 }}>
          <span style={{ color: "#1672B980", fontSize: "16px", fontWeight: 200 }}>Human Resources / </span> <span style={{ fontWeight: 200, fontSize: "16px" }}>KPI's</span>
        </Typography>
        <Box sx={{ width: "100%", height: "1px", background: "#1672B91A", borderRadius: "2px", mb: 0 }} />
        <KpiMetricscards cardsData={Kpiscardsdata} kpiBriefData={kpiBriefData} />

        {/* Section Title: Time Series */}
        <Typography sx={{ fontSize: "16px", mb: 1, mt: 3 }}>
          <span style={{ color: "#1672B980", fontSize: "16px", fontWeight: 200 }}>Human Resources / </span> <span style={{ fontWeight: 200, fontSize: "16px" }}>Time Series</span>
        </Typography>
        <Box sx={{ width: "100%", height: "1px", background: "#1672B91A", borderRadius: "2px", mb: 0 }} />
        <TimeSeriescards timeSeriesCards={timeSeriesCardsdata} />

        <Box sx={{ mt: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
            <Typography sx={{ fontSize: "16px" }}>
              <span style={{ color: "#1672B980", fontWeight: 200, fontSize: "16px" }}>Human Resources / </span> <span style={{ fontWeight: 200, fontSize: "16px" }}>Visualizations</span>
            </Typography>

            <Box sx={{ display: "flex", gap: 1 }}>
              {["YTD", "QTD", "MTD"].map((label, idx) => (
                <FilterChip key={label} label={label} idx={idx} activeIdx={activeIdx} onClick={setActiveIdx} />
              ))}
            </Box>
          </Box>

          <Box sx={{ width: "100%", height: "1px", background: "#1672B91A", borderRadius: "2px", mb: 0 }} />
        </Box>
        <Grid
          container
          rowSpacing={2}
          columnSpacing={2}
          sx={{
            mt: "26px !important",
            alignItems: "stretch", // keeps all cards same height
          }}
        >
          <Grid
            item
            xs={12}
            sm={6}
            md={3}
            sx={{
              pt: { xs: "0px !important", sm: "0px !important", md: "0px !important" },
            }}
          >
            <PieLegendCard title="Headcount by Department" data={headcountByDepartmentPie} />
          </Grid>

          <Grid
            item
            xs={12}
            sm={6}
            md={3}
            sx={{
              pt: { xs: "26px !important", sm: "26px !important", md: "0px !important" },
            }}
          >
            <PieLegendCard title="Retention Rate" data={retentionRatePie} />
          </Grid>

          <Grid
            item
            xs={12}
            sm={6}
            md={3}
            sx={{
              pt: { xs: "26px !important", sm: "26px !important", md: "0px !important" },
            }}
          >
            <PieLegendCard title="Tenure Mix" data={tenureMixPie} />
          </Grid>

          <Grid
            item
            xs={12}
            sm={6}
            md={3}
            sx={{
              pt: { xs: "26px !important", sm: "26px !important", md: "0px !important" },
            }}
          >
            <PieLegendCard title="Hiring Source" data={hiringSourcePie} />
          </Grid>
        </Grid>
        <Grid container spacing={3} alignItems="stretch" sx={{ mt: "10px !important" }}>
          {/* LEFT CARD - USA MAP */}
          <Grid item xs={12} md={6} sx={{ display: "flex" }}>
            <HiringFunnel sx={{ flex: 1 }} title={hiringFunnelDatas.title} data={hiringFunnelDatas.data} />
          </Grid>

          {/* RIGHT CARD - BAR CHART */}
          <Grid item xs={12} md={6} sx={{ display: "flex" }}>
            {applicationByRoleChartData?.title && applicationByRoleChartData?.yLabels?.length > 0 && applicationByRoleChartData?.series?.length > 0 && applicationByRoleChartData?.dataMatrix?.length > 0 && <SkuSingleBarChart sx={{ flex: 1 }} title={applicationByRoleChartData.title} yLabels={applicationByRoleChartData.yLabels} series={applicationByRoleChartData.series} dataMatrix={applicationByRoleChartData.dataMatrix} xTicks={applicationByRoleChartData?.xTicks} showUnit={false} />}
          </Grid>
        </Grid>
        <Grid container sx={{ mt: "34px !important" }}>
          <Grid item xs={12}>
            <Barcharts title={employeeCountByDept.title} xLabels={employeeCountByDept.xLabels} series={employeeCountByDept.series} dataMatrix={employeeCountByDept.dataMatrix} yTicks={employeeCountByDept?.yTicks} rounded={false} yAxisLabel="Count" />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default HumanResourcesDashboard;
